import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate } from 'react-router-dom'
import { Alert, Button, Card, Col, Empty, Radio, Row, Select, Spin, Statistic, Tabs, Typography } from 'antd'
import PageLayout from '@BUP/components/Layout/Page'
import LineChart from '@BUP/components/Dashboard/charts/line-chart.jsx'
import HorizontalBars from '@BUP/components/Dashboard/charts/horizontal-bars.jsx'
import { useCounselorMetrics } from '@BUP/hooks/useCounselorMetrics'
import { useChartRange } from '@BUP/hooks/useChartRange'
import ChartRangeControl, { ChartRangeSummary } from '@BUP/components/Dashboard/charts/chart-range-control.jsx'
import { clampDateRange, filterPointsByDateRange, getSeriesBounds, MAX_DATE_RANGE_IN_DAYS, MAX_DATE_RANGE_IN_DAYS_THREE_YEARS } from '@BUP/utils/date-range'
import { hasMetricsDashboardEnabled, handleSchoolgrades } from '@BUP/services/BraveUp'
import { OrganizationContext } from '@BUP/contexts/OrganizationContext'
import styles from './dashboard.module.scss'

const { TabPane } = Tabs

const SERIES_COLORS = {
  student: '#17bebb',
  teacher: '#4c2d86',
  counselor: '#f5a623'
}

const LEVEL_SERIES_COLORS = ['#17bebb', '#4c2d86', '#f5a623', '#5B8FF9', '#E86452', '#5AD8A6', '#945FB9', '#FF9845']

// Every monthly timeline chart shares this clamp: the payload never carries more
// than 36 months, so one constant replaces the per-chart magic numbers.
const MONTHLY_MAX_DAYS = MAX_DATE_RANGE_IN_DAYS_THREE_YEARS
const LOGIN_MAX_DAYS = MAX_DATE_RANGE_IN_DAYS
const RESOURCES_MAX_DAYS = 364

// Keep the points that fall inside the chart's selected window. Replaces the old
// `points.slice(-period)` pattern with the same date-window model every chart uses.
const withWindow = (series, range) =>
  series.map(entry => ({
    ...entry,
    points: range ? filterPointsByDateRange(entry.points, range[0], range[1]) : entry.points
  }))

const monthlyBounds = timeline => getSeriesBounds(timeline?.map(m => ({ date: `${m.period}-01` })))

// Thin adapters so each card's JSX stays short: the chart's whole `useChartRange`
// result is handed to the shared control instead of repeating six props per chart.
const RangeControl = ({ chartRange }) => {
  const handleRangeChange = dates => chartRange.setRange(dates)
  const handleGranularityChange = value => chartRange.setGranularity(value)

  return (
    <ChartRangeControl
      range={chartRange.range}
      onRangeChange={handleRangeChange}
      disabledDate={chartRange.disabledDate}
      granularities={chartRange.granularities}
      granularity={chartRange.granularity}
      onGranularityChange={handleGranularityChange}
    />
  )
}

const RangeFooter = ({ chartRange, hint }) => (
  <ChartRangeSummary range={chartRange.range} hint={hint} />
)

const getRangeBounds = seriesMap => {
  const points = Object.values(seriesMap ?? {}).flatMap(roleSeries => roleSeries)
  // Empty payload means "not loaded yet": return null so the hook keeps a stable
  // null bounds. Returning fresh `new Date()`s here would change on every render
  // and re-trigger the default-range effect forever.
  if (!points.length) return null

  const dates = points.map(point => new Date(`${point.date}T00:00:00.000Z`)).sort((a, b) => a - b)
  return clampDateRange(dates[0], dates[dates.length - 1])
}

// Empty defaults to prevent crashes in useMemo hooks during loading
const EMPTY_ROLE_SERIES = { student: [], teacher: [], counselor: [] }

const EMPTY_USABILITY = {
  uniqueStudentsResponded: 0,
  bupModesStarted: 0,
  bupModesClosed: 0,
  protocolCasesCreated: 0,
  uniqueStudentsTimeline: [],
  logins: { days: EMPTY_ROLE_SERIES, weeks: EMPTY_ROLE_SERIES, months: EMPTY_ROLE_SERIES },
  resourcesViewed: [],
  resourcesViewedByProfile: { student: [], teacher: [], counselor: [] },
  sociogramsByLevel: {},
  sociogramResultsByLevelTimeline: []
}

const EMPTY_IMPACT = {
  protocolsBySeverityTimeline: [],
  protocolsByTypeTimeline: [],
  physicalAggressionTimeline: [],
  digitalAggressionTimeline: [],
  physicalAggressionMotivesTimeline: [],
  digitalAggressionMotivesTimeline: [],
  bullyingReportsTimeline: [],
  protocolsBySeverity: [],
  physicalAggression: [],
  digitalAggression: [],
  physicalAggressionMotives: [],
  digitalAggressionMotives: [],
  physicalAggressionByYear: []
}

const CounselorDashboard = () => {
  const { t, i18n } = useTranslation()
  const { data, isLoading, isError, error, refetch } = useCounselorMetrics()
  const usability = data?.usability
  const impact = data?.impact
  const staleMinutes = data?.staleMinutes

  // Safe versions for useMemo hooks (won't crash during loading)
  const safeUsability = usability ?? EMPTY_USABILITY
  const safeImpact = impact ?? EMPTY_IMPACT

  // The backend aggregates by the `nivel:N` tag ("nivel:4" for "4° Básico"),
  // optionally falling back to the raw `grade:X` tag on legacy surveys. Resolve
  // it to a country-localized name using the same catalog the rest of the app
  // uses (handleSchoolgrades), with i18n as a second fallback and the raw
  // value as the last resort (never the internal `grade:<uuid>` label).
  const localizeLevel = useCallback(level => {
    if (!level) return level
    if (!level.startsWith('nivel:')) return level
    const gradeNames = handleSchoolgrades(i18n.language)
    const match = gradeNames.find(grade => grade.id === level)
    if (match) return match.name
    const translated = t(`common.resources.course.values.tags.nivel.${level.slice('nivel:'.length)}`)
    return translated && !translated.startsWith('common.resources.course.values.tags.nivel.') ? translated : level
  }, [i18n.language, t])

  // Each timeline chart owns an independent, session-only date window.
  const protocolsRange = useChartRange({
    bounds: monthlyBounds(safeImpact.protocolsBySeverityTimeline),
    maxDays: MONTHLY_MAX_DAYS,
    defaultSpan: { months: 12 }
  })
  const [highlightedSeverity, setHighlightedSeverity] = useState(null)

  const protocolsSeries = useMemo(() => {
    const allSeries = [
      { key: 'il-a', label: t('pages.dashboard.impact.severities.il-a'), color: '#E86452', points: safeImpact.protocolsBySeverityTimeline.map(m => ({ date: `${m.period}-01`, count: m.byType['il-a'] })) },
      { key: 'il-b', label: t('pages.dashboard.impact.severities.il-b'), color: '#F6BD16', points: safeImpact.protocolsBySeverityTimeline.map(m => ({ date: `${m.period}-01`, count: m.byType['il-b'] })) },
      { key: 'il-c', label: t('pages.dashboard.impact.severities.il-c'), color: '#5AD8A6', points: safeImpact.protocolsBySeverityTimeline.map(m => ({ date: `${m.period}-01`, count: m.byType['il-c'] })) }
    ]
    return withWindow(allSeries, protocolsRange.range)
  }, [safeImpact.protocolsBySeverityTimeline, protocolsRange.range, t])

  // State for protocols by type timeline
  const protocolsTypeRange = useChartRange({
    bounds: monthlyBounds(safeImpact.protocolsByTypeTimeline),
    maxDays: MONTHLY_MAX_DAYS,
    defaultSpan: { months: 12 }
  })
  const [highlightedProtocolType, setHighlightedProtocolType] = useState(null)

  const protocolsTypeSeries = useMemo(() => {
    const allSeries = [
      { key: 'vulneracion-derechos', label: t('pages.dashboard.impact.protocolTypeLabels.vulneracion-derechos'), color: '#5B8FF9', points: safeImpact.protocolsByTypeTimeline.map(m => ({ date: `${m.period}-01`, count: m.byType['vulneracion-derechos'] })) },
      { key: 'agresiones-sexuales', label: t('pages.dashboard.impact.protocolTypeLabels.agresiones-sexuales'), color: '#E86452', points: safeImpact.protocolsByTypeTimeline.map(m => ({ date: `${m.period}-01`, count: m.byType['agresiones-sexuales'] })) },
      { key: 'maltrato-acoso', label: t('pages.dashboard.impact.protocolTypeLabels.maltrato-acoso'), color: '#F6BD16', points: safeImpact.protocolsByTypeTimeline.map(m => ({ date: `${m.period}-01`, count: m.byType['maltrato-acoso'] })) },
      { key: 'drogas-alcohol', label: t('pages.dashboard.impact.protocolTypeLabels.drogas-alcohol'), color: '#5AD8A6', points: safeImpact.protocolsByTypeTimeline.map(m => ({ date: `${m.period}-01`, count: m.byType['drogas-alcohol'] })) }
    ]
    return withWindow(allSeries, protocolsTypeRange.range)
  }, [safeImpact.protocolsByTypeTimeline, protocolsTypeRange.range, t])

  // State for aggression timeline charts
  const aggressionRange = useChartRange({
    bounds: monthlyBounds(safeImpact.physicalAggressionTimeline),
    maxDays: MONTHLY_MAX_DAYS,
    defaultSpan: { months: 12 }
  })
  const [highlightedAggressionType, setHighlightedAggressionType] = useState(null)
  const digitalAggressionRange = useChartRange({
    bounds: monthlyBounds(safeImpact.digitalAggressionTimeline),
    maxDays: MONTHLY_MAX_DAYS,
    defaultSpan: { months: 12 }
  })
  const [highlightedDigitalAggressionType, setHighlightedDigitalAggressionType] = useState(null)
  const physicalMotivesRange = useChartRange({
    bounds: monthlyBounds(safeImpact.physicalAggressionMotivesTimeline),
    maxDays: MONTHLY_MAX_DAYS,
    defaultSpan: { months: 12 }
  })
  const [highlightedPhysicalMotivesType, setHighlightedPhysicalMotivesType] = useState(null)
  const digitalMotivesRange = useChartRange({
    bounds: monthlyBounds(safeImpact.digitalAggressionMotivesTimeline),
    maxDays: MONTHLY_MAX_DAYS,
    defaultSpan: { months: 12 }
  })
  const [highlightedDigitalMotivesType, setHighlightedDigitalMotivesType] = useState(null)

  const aggressionSeries = useMemo(() => {
    const allSeries = [
      { key: 'isolation-rejection', label: t('pages.dashboard.impact.aggressionTypes.isolation-rejection'), color: '#5B8FF9', points: safeImpact.physicalAggressionTimeline.map(m => ({ date: `${m.period}-01`, count: m.byType['isolation-rejection'] })) },
      { key: 'insults-threats', label: t('pages.dashboard.impact.aggressionTypes.insults-threats'), color: '#E86452', points: safeImpact.physicalAggressionTimeline.map(m => ({ date: `${m.period}-01`, count: m.byType['insults-threats'] })) },
      { key: 'rumors', label: t('pages.dashboard.impact.aggressionTypes.rumors'), color: '#F6BD16', points: safeImpact.physicalAggressionTimeline.map(m => ({ date: `${m.period}-01`, count: m.byType.rumors })) },
      { key: 'bothering', label: t('pages.dashboard.impact.aggressionTypes.bothering'), color: '#5AD8A6', points: safeImpact.physicalAggressionTimeline.map(m => ({ date: `${m.period}-01`, count: m.byType.bothering })) }
    ]
    return withWindow(allSeries, aggressionRange.range)
  }, [safeImpact.physicalAggressionTimeline, aggressionRange.range, t])

  const digitalAggressionSeries = useMemo(() => {
    const allSeries = [
      { key: 'memes-stickers-videos', label: t('pages.dashboard.impact.aggressionTypes.memes-stickers-videos'), color: '#5B8FF9', points: safeImpact.digitalAggressionTimeline.map(m => ({ date: `${m.period}-01`, count: m.byType['memes-stickers-videos'] })) },
      { key: 'rumors', label: t('pages.dashboard.impact.aggressionTypes.rumors'), color: '#F6BD16', points: safeImpact.digitalAggressionTimeline.map(m => ({ date: `${m.period}-01`, count: m.byType.rumors })) },
      { key: 'isolation-rejection', label: t('pages.dashboard.impact.aggressionTypes.isolation-rejection'), color: '#5AD8A6', points: safeImpact.digitalAggressionTimeline.map(m => ({ date: `${m.period}-01`, count: m.byType['isolation-rejection'] })) },
      { key: 'discrimination', label: t('pages.dashboard.impact.aggressionTypes.discrimination'), color: '#E86452', points: safeImpact.digitalAggressionTimeline.map(m => ({ date: `${m.period}-01`, count: m.byType.discrimination })) }
    ]
    return withWindow(allSeries, digitalAggressionRange.range)
  }, [safeImpact.digitalAggressionTimeline, digitalAggressionRange.range, t])

  const physicalMotivesSeries = useMemo(() => {
    const allSeries = [
      { key: 'personality', label: t('pages.dashboard.impact.aggressionMotives.personality'), color: '#5B8FF9', points: safeImpact.physicalAggressionMotivesTimeline.map(m => ({ date: `${m.period}-01`, count: m.byType.personality })) },
      { key: 'physical-traits', label: t('pages.dashboard.impact.aggressionMotives.physical-traits'), color: '#E86452', points: safeImpact.physicalAggressionMotivesTimeline.map(m => ({ date: `${m.period}-01`, count: m.byType['physical-traits'] })) },
      { key: 'tastes', label: t('pages.dashboard.impact.aggressionMotives.tastes'), color: '#F6BD16', points: safeImpact.physicalAggressionMotivesTimeline.map(m => ({ date: `${m.period}-01`, count: m.byType.tastes })) },
      { key: 'dont-know', label: t('pages.dashboard.impact.aggressionMotives.dont-know'), color: '#5AD8A6', points: safeImpact.physicalAggressionMotivesTimeline.map(m => ({ date: `${m.period}-01`, count: m.byType['dont-know'] })) }
    ]
    return withWindow(allSeries, physicalMotivesRange.range)
  }, [safeImpact.physicalAggressionMotivesTimeline, physicalMotivesRange.range, t])

  const digitalMotivesSeries = useMemo(() => {
    const allSeries = [
      { key: 'personality', label: t('pages.dashboard.impact.aggressionMotives.personality'), color: '#5B8FF9', points: safeImpact.digitalAggressionMotivesTimeline.map(m => ({ date: `${m.period}-01`, count: m.byType.personality })) },
      { key: 'physical-traits', label: t('pages.dashboard.impact.aggressionMotives.physical-traits'), color: '#E86452', points: safeImpact.digitalAggressionMotivesTimeline.map(m => ({ date: `${m.period}-01`, count: m.byType['physical-traits'] })) },
      { key: 'tastes', label: t('pages.dashboard.impact.aggressionMotives.tastes'), color: '#F6BD16', points: safeImpact.digitalAggressionMotivesTimeline.map(m => ({ date: `${m.period}-01`, count: m.byType.tastes })) },
      { key: 'dont-know', label: t('pages.dashboard.impact.aggressionMotives.dont-know'), color: '#5AD8A6', points: safeImpact.digitalAggressionMotivesTimeline.map(m => ({ date: `${m.period}-01`, count: m.byType['dont-know'] })) }
    ]
    return withWindow(allSeries, digitalMotivesRange.range)
  }, [safeImpact.digitalAggressionMotivesTimeline, digitalMotivesRange.range, t])

  // Unique students timeline: full payload span by default, clamped to 3 years.
  const studentsRange = useChartRange({
    bounds: getSeriesBounds(safeUsability.uniqueStudentsTimeline),
    maxDays: MONTHLY_MAX_DAYS
  })

  const uniqueStudentsSeries = useMemo(() => withWindow([{
    key: 'unique-students',
    label: t('pages.dashboard.usability.uniqueStudentsTimeline'),
    color: '#17bebb',
    points: safeUsability.uniqueStudentsTimeline
  }], studentsRange.range), [studentsRange.range, t, safeUsability.uniqueStudentsTimeline])

  // Courses with sociogram results timeline (X = time, Y = results per level/course)
  const sociogramResultsBounds = useMemo(
    () => getSeriesBounds(
      safeUsability.sociogramResultsByLevelTimeline.flatMap(entry => entry.points ?? [])
    ),
    [safeUsability.sociogramResultsByLevelTimeline]
  )
  const sociogramResultsRange = useChartRange({
    bounds: sociogramResultsBounds,
    maxDays: MONTHLY_MAX_DAYS,
    defaultSpan: { months: 36 }
  })
  const [highlightedSociogramLevel, setHighlightedSociogramLevel] = useState(null)

  const sociogramResultsSeries = useMemo(
    () => withWindow(
      safeUsability.sociogramResultsByLevelTimeline.map((entry, index) => ({
        key: entry.level,
        label: localizeLevel(entry.level),
        color: LEVEL_SERIES_COLORS[index % LEVEL_SERIES_COLORS.length],
        points: entry.points
      })),
      sociogramResultsRange.range
    ),
    [sociogramResultsRange.range, safeUsability.sociogramResultsByLevelTimeline, localizeLevel]
  )

  // Bullying and cyberbullying reports timeline
  const bullyingReportsRange = useChartRange({
    bounds: monthlyBounds(safeImpact.bullyingReportsTimeline),
    maxDays: MONTHLY_MAX_DAYS,
    defaultSpan: { months: 12 }
  })

  const bullyingReportsSeries = useMemo(() => withWindow([
    { key: 'bullying', label: t('pages.dashboard.impact.bullyingReports.bullying'), color: '#5B8FF9', points: safeImpact.bullyingReportsTimeline.map(m => ({ date: `${m.period}-01`, count: m.byType.bullying })) },
    { key: 'cyberbullying', label: t('pages.dashboard.impact.bullyingReports.cyberbullying'), color: '#5AD8A6', points: safeImpact.bullyingReportsTimeline.map(m => ({ date: `${m.period}-01`, count: m.byType.cyberbullying })) }
  ], bullyingReportsRange.range), [bullyingReportsRange.range, safeImpact.bullyingReportsTimeline, t])

  // Logins is the only chart whose payload carries several granularities, so it
  // owns the granularity toggle. Its bounds depend on the selected granularity,
  // which is why `bounds` is a function here.
  const loginRange = useChartRange({
    bounds: granularity => {
      const roleSeriesMap = safeUsability.logins[granularity] ?? EMPTY_ROLE_SERIES
      return getRangeBounds(roleSeriesMap)
    },
    maxDays: LOGIN_MAX_DAYS,
    granularities: ['days', 'weeks', 'months'],
    defaultGranularity: 'weeks'
  })
  // Older API payloads may not include the requested granularity yet
  const loginSeriesByRole = safeUsability.logins[loginRange.granularity] ?? EMPTY_ROLE_SERIES

  const loginSeries = useMemo(() => withWindow(
    Object.entries(loginSeriesByRole).map(([roleKey, points]) => ({
      key: roleKey,
      label: t(`pages.dashboard.usability.roles.${roleKey}`),
      color: SERIES_COLORS[roleKey],
      points
    })),
    loginRange.range
  ), [loginRange.range, loginSeriesByRole, t])

  const sociogramYears = useMemo(
    () => Object.keys(safeUsability.sociogramsByLevel).sort((a, b) => Number(b) - Number(a)),
    [safeUsability.sociogramsByLevel]
  )
  const [sociogramYear, setSociogramYear] = useState(sociogramYears[0])
  const [sociogramOption, setSociogramOption] = useState('both')

  // sociogramYears arrives asynchronously from the API, but the useState
  // initializer above only runs on the first render (while the list is still
  // empty, so the selection would stay undefined forever). Re-sync the
  // selection once real years are available, or whenever the currently
  // selected year is no longer part of the list (e.g. after a refetch).
  useEffect(() => {
    setSociogramYear(current =>
      current && sociogramYears.includes(current) ? current : sociogramYears[0]
    )
  }, [sociogramYears])

  const buildSociogramRows = number => (safeUsability.sociogramsByLevel[sociogramYear] ?? []).map(entry => {
    const data = entry.sociograms.find(sociogram => sociogram.number === number)
    return {
      label: localizeLevel(entry.level),
      values: [
        { key: 'opened', label: t('pages.dashboard.usability.sociogramStates.opened'), value: data?.opened ?? 0, color: '#17bebb' },
        { key: 'finalized', label: t('pages.dashboard.usability.sociogramStates.finalized'), value: data?.finalized ?? 0, color: '#4c2d86' },
        { key: 'finalizedWithResults', label: t('pages.dashboard.usability.sociogramStates.finalizedWithResults'), value: data?.finalizedWithResults ?? 0, color: '#f5a623' }
      ]
    }
  })

  // Resources viewed: weekly payload, so the window is picked in weeks.
  const resourcesRange = useChartRange({
    bounds: getSeriesBounds(safeUsability.resourcesViewed),
    maxDays: RESOURCES_MAX_DAYS,
    defaultSpan: { weeks: 52 },
    defaultGranularity: 'weeks'
  })

  const resourceSeries = useMemo(() => withWindow([
    {
      key: 'total',
      label: t('pages.dashboard.usability.total'),
      color: '#17bebb',
      points: safeUsability.resourcesViewed
    },
    ...Object.entries(safeUsability.resourcesViewedByProfile).map(([roleKey, points]) => ({
      key: roleKey,
      label: t(`pages.dashboard.usability.roles.${roleKey}`),
      color: SERIES_COLORS[roleKey],
      points
    }))
  ], resourcesRange.range), [resourcesRange.range, t, safeUsability.resourcesViewed, safeUsability.resourcesViewedByProfile])

  if (isLoading) {
    return (
      <PageLayout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <Spin size='large' />
        </div>
      </PageLayout>
    )
  }

  if (isError) {
    return (
      <PageLayout>
        <Alert
          message={t('pages.dashboard.errorTitle', 'Error al cargar métricas')}
          description={error?.message || t('pages.dashboard.errorDescription', 'Ha ocurrido un error al cargar los datos del dashboard')}
          type='error'
          showIcon
          action={
            <Button size='small' onClick={refetch}>
              {t('pages.dashboard.retry', 'Reintentar')}
            </Button>
          }
        />
      </PageLayout>
    )
  }

  return (
    <PageLayout>
      <div className={styles.dashboard_container}>
        <Typography.Title level={1} className={styles.title}>
          {t('pages.dashboard.title')}
          {staleMinutes && staleMinutes > 5 && (
            <Typography.Text type='secondary' style={{ fontSize: 12, marginLeft: 8 }}>
              {t('pages.dashboard.staleData', 'Datos de hace {{minutes}} minutos', { minutes: Math.round(staleMinutes) })}
            </Typography.Text>
          )}
        </Typography.Title>
        <Tabs defaultActiveKey='usability'>
          <TabPane tab={t('pages.dashboard.tabs.usability')} key='usability'>
            {!usability
              ? (
                <Empty description={t('pages.dashboard.noUsabilityData', 'No hay datos de usabilidad disponibles')} />
                )
              : (
                <>
                  <Row gutter={[16, 16]}>
                    <Col xs={12} md={6}>
                      <Card><Statistic title={t('pages.dashboard.usability.uniqueStudents')} value={usability.uniqueStudentsResponded} /></Card>
                    </Col>
                    <Col xs={12} md={6}>
                      <Card><Statistic title={t('pages.dashboard.usability.bupModesStarted')} value={usability.bupModesStarted} /></Card>
                    </Col>
                    <Col xs={12} md={6}>
                      <Card><Statistic title={t('pages.dashboard.usability.bupModesClosed')} value={usability.bupModesClosed} /></Card>
                    </Col>
                    <Col xs={12} md={6}>
                      <Card><Statistic title={t('pages.dashboard.usability.protocolCasesCreated')} value={usability.protocolCasesCreated} /></Card>
                    </Col>
                  </Row>
                  <Card
                    title={t('pages.dashboard.usability.uniqueStudentsTimeline')}
                    extra={<RangeControl chartRange={studentsRange} />}
                    className={styles.chart_card}
                  >
                    <LineChart series={uniqueStudentsSeries} rangeType='months' />
                    <RangeFooter
                      chartRange={studentsRange}
                      hint={t('pages.dashboard.usability.dateRange.maxThreeYears')}
                    />
                  </Card>
                  <Card
                    title={t('pages.dashboard.usability.logins')}
                    extra={<RangeControl chartRange={loginRange} />}
                    className={styles.chart_card}
                  >
                    <LineChart series={loginSeries} rangeType={loginRange.granularity} />
                    <RangeFooter
                      chartRange={loginRange}
                      hint={t('pages.dashboard.usability.dateRange.maxOneYear')}
                    />
                  </Card>
                  <Card
                    title={t('pages.dashboard.usability.resourcesViewed')}
                    extra={<RangeControl chartRange={resourcesRange} />}
                    className={styles.chart_card}
                  >
                    <LineChart series={resourceSeries} rangeType='weeks' />
                    <RangeFooter
                      chartRange={resourcesRange}
                      hint={t('pages.dashboard.usability.dateRange.maxOneYear')}
                    />
                  </Card>
                  <Card
                    title={t('pages.dashboard.usability.sociogramsByLevel')}
                    extra={
                      <div className={styles.date_range_controls}>
                        <Select
                          size='small'
                          value={sociogramYear}
                          onChange={setSociogramYear}
                          options={sociogramYears.map(year => ({ value: year, label: year }))}
                        />
                        <Radio.Group
                          size='small'
                          value={sociogramOption}
                          onChange={event => setSociogramOption(event.target.value)}
                        >
                          <Radio.Button value='1'>{t('pages.dashboard.usability.sociogramNumbers.first')}</Radio.Button>
                          <Radio.Button value='2'>{t('pages.dashboard.usability.sociogramNumbers.second')}</Radio.Button>
                          <Radio.Button value='both'>{t('pages.dashboard.usability.sociogramNumbers.both')}</Radio.Button>
                        </Radio.Group>
                      </div>
              }
                    className={styles.chart_card}
                  >
                    {sociogramOption === 'both'
                      ? (
                        <div className={styles.small_multiples}>
                          {[1, 2].map(number => (
                            <div key={number} className={styles.small_multiple}>
                              <Typography.Text strong className={styles.small_multiple_title}>
                                {t('pages.dashboard.usability.sociogramNumbers.title', { number })}
                              </Typography.Text>
                              <HorizontalBars rows={buildSociogramRows(number)} />
                            </div>
                          ))}
                        </div>
                        )
                      : (
                        <HorizontalBars rows={buildSociogramRows(Number(sociogramOption))} />
                        )}
                  </Card>
                  <Card
                    title={t('pages.dashboard.usability.coursesWithSociogramResults')}
                    extra={
                      <div className={styles.date_range_controls}>
                        <RangeControl chartRange={sociogramResultsRange} />
                        <Select
                          size='small'
                          value={highlightedSociogramLevel}
                          onChange={setHighlightedSociogramLevel}
                          placeholder={t('pages.dashboard.usability.highlightLevel')}
                          allowClear
                          options={sociogramResultsSeries.map(s => ({ value: s.key, label: s.label }))}
                          style={{ width: 200 }}
                        />
                      </div>
              }
                    className={styles.chart_card}
                  >
                    <LineChart
                      series={sociogramResultsSeries}
                      rangeType='months'
                      highlightedSeries={highlightedSociogramLevel}
                    />
                    <RangeFooter
                      chartRange={sociogramResultsRange}
                      hint={t('pages.dashboard.usability.dateRange.maxThreeYears')}
                    />
                  </Card>
                </>
                )}
          </TabPane>
          <TabPane tab={t('pages.dashboard.tabs.impact')} key='impact'>
            {!impact
              ? (
                <Empty description={t('pages.dashboard.noImpactData', 'No hay datos de impacto disponibles')} />
                )
              : (
                <>
                  <Card
                    title={t('pages.dashboard.impact.protocolsBySeverity')}
                    className={styles.chart_card}
                    extra={
                      <div className={styles.date_range_controls}>
                        <RangeControl chartRange={protocolsRange} />
                        <Select
                          size='small'
                          value={highlightedSeverity}
                          onChange={setHighlightedSeverity}
                          placeholder={t('pages.dashboard.impact.highlightSeverity')}
                          allowClear
                          options={[
                            { value: 'il-a', label: t('pages.dashboard.impact.severities.il-a') },
                            { value: 'il-b', label: t('pages.dashboard.impact.severities.il-b') },
                            { value: 'il-c', label: t('pages.dashboard.impact.severities.il-c') }
                          ]}
                          style={{ width: 200 }}
                        />
                      </div>
              }
                  >
                    <LineChart
                      series={protocolsSeries}
                      rangeType='months'
                      highlightedSeries={highlightedSeverity}
                    />
                    <RangeFooter
                      chartRange={protocolsRange}
                      hint={t('pages.dashboard.usability.dateRange.maxThreeYears')}
                    />
                  </Card>

                  {/* Evolución temporal: Tipos de protocolo */}
                  <Card
                    title={t('pages.dashboard.impact.protocolTypes')}
                    className={styles.chart_card}
                    extra={
                      <div className={styles.date_range_controls}>
                        <RangeControl chartRange={protocolsTypeRange} />
                        <Select
                          size='small'
                          value={highlightedProtocolType}
                          onChange={setHighlightedProtocolType}
                          placeholder={t('pages.dashboard.impact.highlightType')}
                          allowClear
                          options={[
                            { value: 'vulneracion-derechos', label: t('pages.dashboard.impact.protocolTypeLabels.vulneracion-derechos') },
                            { value: 'agresiones-sexuales', label: t('pages.dashboard.impact.protocolTypeLabels.agresiones-sexuales') },
                            { value: 'maltrato-acoso', label: t('pages.dashboard.impact.protocolTypeLabels.maltrato-acoso') },
                            { value: 'drogas-alcohol', label: t('pages.dashboard.impact.protocolTypeLabels.drogas-alcohol') }
                          ]}
                          style={{ width: 200 }}
                        />
                      </div>
              }
                  >
                    <LineChart
                      series={protocolsTypeSeries}
                      rangeType='months'
                      highlightedSeries={highlightedProtocolType}
                    />
                    <RangeFooter
                      chartRange={protocolsTypeRange}
                      hint={t('pages.dashboard.usability.dateRange.maxThreeYears')}
                    />
                  </Card>

                  {/* Evolución temporal: Multi-línea por tipo */}
                  <Card
                    title={t('pages.dashboard.impact.physicalAggression')}
                    className={styles.chart_card}
                    extra={
                      <div className={styles.date_range_controls}>
                        <RangeControl chartRange={aggressionRange} />
                        <Select
                          size='small'
                          value={highlightedAggressionType}
                          onChange={setHighlightedAggressionType}
                          placeholder={t('pages.dashboard.impact.highlightType')}
                          allowClear
                          options={[
                            { value: 'isolation-rejection', label: t('pages.dashboard.impact.aggressionTypes.isolation-rejection') },
                            { value: 'insults-threats', label: t('pages.dashboard.impact.aggressionTypes.insults-threats') },
                            { value: 'rumors', label: t('pages.dashboard.impact.aggressionTypes.rumors') },
                            { value: 'bothering', label: t('pages.dashboard.impact.aggressionTypes.bothering') }
                          ]}
                          style={{ width: 200 }}
                        />
                      </div>
              }
                  >
                    <LineChart
                      series={aggressionSeries}
                      rangeType='months'
                      highlightedSeries={highlightedAggressionType}
                    />
                    <RangeFooter
                      chartRange={aggressionRange}
                      hint={t('pages.dashboard.usability.dateRange.maxThreeYears')}
                    />
                  </Card>

                  {/* Evolución temporal: Agresión digital */}
                  <Card
                    title={t('pages.dashboard.impact.digitalAggression')}
                    className={styles.chart_card}
                    extra={
                      <div className={styles.date_range_controls}>
                        <RangeControl chartRange={digitalAggressionRange} />
                        <Select
                          size='small'
                          value={highlightedDigitalAggressionType}
                          onChange={setHighlightedDigitalAggressionType}
                          placeholder={t('pages.dashboard.impact.highlightType')}
                          allowClear
                          options={[
                            { value: 'memes-stickers-videos', label: t('pages.dashboard.impact.aggressionTypes.memes-stickers-videos') },
                            { value: 'rumors', label: t('pages.dashboard.impact.aggressionTypes.rumors') },
                            { value: 'isolation-rejection', label: t('pages.dashboard.impact.aggressionTypes.isolation-rejection') },
                            { value: 'discrimination', label: t('pages.dashboard.impact.aggressionTypes.discrimination') }
                          ]}
                          style={{ width: 200 }}
                        />
                      </div>
              }
                  >
                    <LineChart
                      series={digitalAggressionSeries}
                      rangeType='months'
                      highlightedSeries={highlightedDigitalAggressionType}
                    />
                    <RangeFooter
                      chartRange={digitalAggressionRange}
                      hint={t('pages.dashboard.usability.dateRange.maxThreeYears')}
                    />
                  </Card>

                  {/* Evolución temporal: Motivos agresión presencial */}
                  <Card
                    title={t('pages.dashboard.impact.physicalAggressionMotives')}
                    className={styles.chart_card}
                    extra={
                      <div className={styles.date_range_controls}>
                        <RangeControl chartRange={physicalMotivesRange} />
                        <Select
                          size='small'
                          value={highlightedPhysicalMotivesType}
                          onChange={setHighlightedPhysicalMotivesType}
                          placeholder={t('pages.dashboard.impact.highlightMotive')}
                          allowClear
                          options={[
                            { value: 'personality', label: t('pages.dashboard.impact.aggressionMotives.personality') },
                            { value: 'physical-traits', label: t('pages.dashboard.impact.aggressionMotives.physical-traits') },
                            { value: 'tastes', label: t('pages.dashboard.impact.aggressionMotives.tastes') },
                            { value: 'dont-know', label: t('pages.dashboard.impact.aggressionMotives.dont-know') }
                          ]}
                          style={{ width: 200 }}
                        />
                      </div>
              }
                  >
                    <LineChart
                      series={physicalMotivesSeries}
                      rangeType='months'
                      highlightedSeries={highlightedPhysicalMotivesType}
                    />
                    <RangeFooter
                      chartRange={physicalMotivesRange}
                      hint={t('pages.dashboard.usability.dateRange.maxThreeYears')}
                    />
                  </Card>

                  {/* Evolución temporal: Motivos agresión digital */}
                  <Card
                    title={t('pages.dashboard.impact.digitalAggressionMotives')}
                    className={styles.chart_card}
                    extra={
                      <div className={styles.date_range_controls}>
                        <RangeControl chartRange={digitalMotivesRange} />
                        <Select
                          size='small'
                          value={highlightedDigitalMotivesType}
                          onChange={setHighlightedDigitalMotivesType}
                          placeholder={t('pages.dashboard.impact.highlightMotive')}
                          allowClear
                          options={[
                            { value: 'personality', label: t('pages.dashboard.impact.aggressionMotives.personality') },
                            { value: 'physical-traits', label: t('pages.dashboard.impact.aggressionMotives.physical-traits') },
                            { value: 'tastes', label: t('pages.dashboard.impact.aggressionMotives.tastes') },
                            { value: 'dont-know', label: t('pages.dashboard.impact.aggressionMotives.dont-know') }
                          ]}
                          style={{ width: 200 }}
                        />
                      </div>
              }
                  >
                    <LineChart
                      series={digitalMotivesSeries}
                      rangeType='months'
                      highlightedSeries={highlightedDigitalMotivesType}
                    />
                    <RangeFooter
                      chartRange={digitalMotivesRange}
                      hint={t('pages.dashboard.usability.dateRange.maxThreeYears')}
                    />
                  </Card>

                  {/* Evolución temporal: Denuncias de bullying y ciberbullying */}
                  <Card
                    title={t('pages.dashboard.impact.bullyingReports.title')}
                    className={styles.chart_card}
                    extra={
                      <div className={styles.date_range_controls}>
                        <RangeControl chartRange={bullyingReportsRange} />
                      </div>
              }
                  >
                    <LineChart
                      series={bullyingReportsSeries}
                      rangeType='months'
                    />
                    <RangeFooter
                      chartRange={bullyingReportsRange}
                      hint={t('pages.dashboard.usability.dateRange.maxThreeYears')}
                    />
                  </Card>
                </>
                )}
          </TabPane>
        </Tabs>
      </div>
    </PageLayout>
  )
}

// Visibility guard (showMetricsDashboard): fails closed and redirects home when the
// organization flag is off/missing. Kept as a wrapper so the dashboard's hooks stay
// unconditional (react-hooks/rules-of-hooks); the metrics hook never runs when blocked.
const CounselorDashboardPage = () => {
  const organization = useContext(OrganizationContext)

  if (!hasMetricsDashboardEnabled(organization)) {
    return <Navigate to='/' replace />
  }

  return <CounselorDashboard />
}

export default CounselorDashboardPage
